import React, { Fragment, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet, PageHeadingContext, Mui } from "@osu/react-ui";
import { FORCED_AUTHENTICATION } from "../../util/constants";

function LoadingSpinner() {
  return (
    <Mui.Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '10rem',
      }}
    >
      <Mui.CircularProgress />
    </Mui.Box>
  );
}

export function RenderableView({
  pathInformation = {
    props: {},
    noIndex: false,
    authRequired: false,
  },
  setPageAppHeader,
  setContainerSize,
}) {
  const {
    props = {},
    Component,
    noIndex,
    title: pathTitle,
    titleProps,
    containerSize = false,
  } = pathInformation;
  const { setHeading, setOptions } = useContext(PageHeadingContext);
  const username = useSelector((state) => state?.authentication?.user?.name);
  const reportName = useSelector((state) => state?.athenaReportReducer?.reportName);
  const authStatus = useSelector((state) => state?.authentication?.status);
  const isLoggedIn = !!useSelector((state) => state?.authentication?.user?.osuid);
  
  const renderProps = { username, reportName };
  const title = !isLoggedIn
    ? "Loading..."
    : typeof pathTitle === "function" ? pathTitle(renderProps) : pathTitle;
  const authRequired = pathInformation.authRequired;

  useEffect(() => {
    title && setHeading(title)
    return () => {
      setHeading("")
    }
  }, [setHeading, title])

  useEffect(() => {
    setContainerSize(containerSize)
  }, [setContainerSize, containerSize])

  useEffect(() => {
    const options = Object.assign(
      { variant: "h2", className: "margin-bottom-2" },
      titleProps
    );
    setOptions(options);
  }, [setOptions, titleProps]);

  // Route-level auth gating: show spinner while auth is resolving
  if (!isLoggedIn && FORCED_AUTHENTICATION) {
    // If auth explicitly failed, redirect to login
    if (authStatus === 'error' || authStatus === 'cleared') {
      return <Navigate to="/login" state={{ redirectPath: window.location.pathname + window.location.search + window.location.hash }} />;
    }
    // Still loading / checking — show inline spinner
    return <LoadingSpinner />;
  }

  return (
    <Fragment>
      {(noIndex || authRequired) && (
        <Helmet>
          <meta id="meta-hidden" name="robots" content="noindex" />
        </Helmet>
      )}
      <Component
        setPageAppHeader={setPageAppHeader}
        {...renderProps}
        {...props}
      />
    </Fragment>
  );
}

RenderableView.propTypes = {
  pathInformation: PropTypes.shape({
    path: PropTypes.string.isRequired,
    props: PropTypes.object,
    // title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    // titleProps: PropTypes.object,
    Component: PropTypes.oneOfType([PropTypes.element, PropTypes.object])
      .isRequired,
    noIndex: PropTypes.bool,
    authRequired: PropTypes.bool,
  }).isRequired,
  renderProps: PropTypes.object,
  createTitle: PropTypes.func,
};

export default RenderableView;
